import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  ClipboardList,
  Download,
  FileText,
  Gift,
  Layers,
  Loader2,
  Smartphone,
  Sparkles,
  Target,
} from 'lucide-react';
import { BOOK_BUNDLES } from '../data/bookBundles';
import {
  createMetaEventId,
  getMetaBrowserIds,
  trackBookInitiateCheckout,
  trackBookPurchase,
  trackBookViewContent,
} from '../lib/metaPixel';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

const BooksPage = () => {
  const [searchParams] = useSearchParams();
  const [loadingSlug, setLoadingSlug] = useState(null);
  const [error, setError] = useState('');
  const [checkoutBook, setCheckoutBook] = useState(null);
  const successSlug = searchParams.get('success');
  const sessionId = searchParams.get('session_id');
  const purchaseEventId = searchParams.get('meta_event_id');
  const [delivery, setDelivery] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');

  const featured = useMemo(() => BOOK_BUNDLES.slice(0, 6), []);
  const otherBooks = useMemo(() => BOOK_BUNDLES.slice(6), []);
  const allBooks = useMemo(() => [...featured, ...otherBooks], [featured, otherBooks]);

  const fetchClientSecret = useCallback(async () => {
    if (!checkoutBook) {
      throw new Error('Choose a book before opening checkout.');
    }

    const res = await fetch('/.netlify/functions/create-book-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slug: checkoutBook.slug,
        metaEventId: checkoutBook.metaEventId,
        metaBrowserIds: getMetaBrowserIds(),
        sourceUrl: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
        returnUrl: `${window.location.origin}/books?success=${checkoutBook.slug}&session_id={CHECKOUT_SESSION_ID}&meta_event_id=${checkoutBook.metaEventId}`,
      }),
    });
    const data = await res.json();

    if (!res.ok || !data.client_secret) {
      throw new Error(data.error || 'Checkout is unavailable right now.');
    }

    return data.client_secret;
  }, [checkoutBook]);

  const startCheckout = (book) => {
    const metaEventId = createMetaEventId(`book-${book.slug}`);
    setError('');
    setLoadingSlug(book.slug);
    trackBookInitiateCheckout(book, metaEventId);
    setCheckoutBook({ ...book, metaEventId });
  };

  const closeCheckout = () => {
    setCheckoutBook(null);
    setLoadingSlug(null);
  };

  useEffect(() => {
    if (!successSlug || !sessionId) return;

    let isMounted = true;
    setDeliveryLoading(true);
    setDeliveryError('');

    fetch('/.netlify/functions/get-book-delivery', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Could not verify your purchase.');
        }
        return data.delivery;
      })
      .then((verifiedDelivery) => {
        if (isMounted) setDelivery(verifiedDelivery);
      })
      .catch((err) => {
        if (isMounted) setDeliveryError(err.message);
      })
      .finally(() => {
        if (isMounted) setDeliveryLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [successSlug, sessionId]);

  useEffect(() => {
    const trackVisibleBook = () => {
      const slug = window.location.hash.replace(/^#(?:details-)?/, '');
      const book = allBooks.find((item) => item.slug === slug);
      if (book) trackBookViewContent(book);
    };

    trackVisibleBook();
    window.addEventListener('hashchange', trackVisibleBook);
    return () => window.removeEventListener('hashchange', trackVisibleBook);
  }, [allBooks]);

  useEffect(() => {
    if (!delivery || !successSlug || !sessionId) return;

    const storageKey = `meta-purchase-${sessionId}`;
    if (sessionStorage.getItem(storageKey)) return;

    const book = allBooks.find((item) => item.slug === successSlug);
    if (!book) return;

    trackBookPurchase(book, purchaseEventId || sessionId);
    sessionStorage.setItem(storageKey, 'tracked');
  }, [allBooks, delivery, purchaseEventId, sessionId, successSlug]);

  const BookCard = ({ book }) => (
    <article id={book.slug} className="group scroll-mt-24 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:shadow-gray-200/60 dark:hover:shadow-black/20 transition-all">
      <div className="grid sm:grid-cols-[180px_1fr] h-full">
        <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 p-4 flex items-center justify-center">
          <img
            src={book.image}
            alt={`${book.title} cover`}
            className="w-full max-w-[160px] aspect-[2/3] object-cover rounded-lg shadow-lg ring-1 ring-black/10 group-hover:scale-[1.02] transition-transform"
            loading="lazy"
          />
        </div>
        <div className="p-5 flex flex-col">
          <div className="flex items-start justify-between gap-3 mb-2">
            <span className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
              {book.category}
            </span>
            <span className="text-lg font-black text-gray-900 dark:text-white">${book.price}</span>
          </div>

          <h2 className="text-xl font-black text-gray-900 dark:text-white leading-tight">{book.title}</h2>
          <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">{book.subtitle}</p>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{book.description}</p>

          <ul className="mt-4 space-y-2">
            {book.highlights.map((item) => (
              <li key={item} className="flex gap-2 text-sm text-gray-700 dark:text-gray-300">
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Delivered as a Kindle and Apple Books compatible digital book plus printable PDF.
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={book.slug === 'macros-made-easy-for-women' ? '/books/macros-made-easy-for-women' : `#details-${book.slug}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-800 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-100 dark:hover:bg-gray-800"
              >
                View what is inside
                <ArrowRight className="h-4 w-4" />
              </a>
              <button
                type="button"
                onClick={() => startCheckout(book)}
                disabled={loadingSlug === book.slug}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-70"
              >
                {loadingSlug === book.slug ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening checkout
                  </>
                ) : (
                  <>
                    Buy bundle
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );

  const DetailList = ({ icon: Icon, title, items }) => (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        <h3 className="text-sm font-black uppercase tracking-wide text-gray-900 dark:text-white">{title}</h3>
      </div>
      <ul className="mt-4 space-y-2">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
            <CheckCircle className="mt-1 h-4 w-4 flex-shrink-0 text-emerald-500" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const BookDetailPanel = ({ book, index }) => (
    <article id={`details-${book.slug}`} className="scroll-mt-24 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="grid lg:grid-cols-[320px_1fr]">
        <div className="bg-gradient-to-br from-slate-100 via-indigo-50 to-emerald-50 p-6 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
          <img
            src={book.image}
            alt={`${book.title} cover`}
            className="mx-auto aspect-[2/3] w-full max-w-[240px] rounded-2xl object-cover shadow-2xl ring-1 ring-black/10"
            loading="lazy"
          />
          <div className="mt-5 rounded-2xl bg-white/80 p-4 text-sm text-gray-700 shadow-sm ring-1 ring-black/5 dark:bg-gray-950/70 dark:text-gray-300">
            <p className="font-black text-gray-900 dark:text-white">Best for</p>
            <p className="mt-1 leading-6">{book.bestFor}</p>
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-600 dark:text-indigo-400">
                Book {index + 1} / {allBooks.length} - {book.category}
              </p>
              <h2 className="mt-2 text-3xl font-black leading-tight text-gray-900 dark:text-white">{book.title}</h2>
              <p className="mt-2 text-base font-semibold text-gray-500 dark:text-gray-400">{book.subtitle}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 px-4 py-3 text-right dark:bg-gray-950">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Bundle price</p>
              <p className="text-3xl font-black text-gray-900 dark:text-white">${book.price}</p>
            </div>
          </div>

          <div className="mt-6 rounded-2xl bg-indigo-50 p-5 dark:bg-indigo-950/40">
            <p className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
              <Target className="h-4 w-4" />
              What this helps you do
            </p>
            <p className="mt-3 text-lg font-semibold leading-8 text-gray-900 dark:text-white">{book.promise}</p>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <DetailList icon={Layers} title="Inside the book" items={book.inside} />
            <DetailList icon={FileText} title="Included tools" items={book.tools} />
            <DetailList icon={ClipboardList} title="Reader wins" items={book.outcomes} />
          </div>

          <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-800 dark:bg-gray-950 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-black text-gray-900 dark:text-white">{book.checkoutLabel}</p>
              <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Download the files after checkout. Health bundles also include app access so the plan can move from reading into daily tracking.
              </p>
            </div>
            <button
              type="button"
              onClick={() => startCheckout(book)}
              disabled={loadingSlug === book.slug}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3 text-sm font-black text-white hover:bg-gray-800 disabled:opacity-70 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {loadingSlug === book.slug ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
              Get this bundle
            </button>
          </div>
        </div>
      </div>
    </article>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white">
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-12 w-12 rounded-full object-cover" />
            <span className="font-black">Integrative Health Journal</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/login?tab=book" className="hidden sm:inline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              I have a book code
            </Link>
            <Link to="/login?tab=signup" className="rounded-lg bg-gray-900 dark:bg-white px-4 py-2 font-semibold text-white dark:text-gray-900">
              Open app
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="px-4 sm:px-6 pt-16 pb-10">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-900/30 px-4 py-1.5 text-sm font-bold text-emerald-700 dark:text-emerald-300 mb-5">
                <Sparkles className="h-4 w-4" />
                Digital book bundles
              </div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-tight">
                Read the guide, print the worksheets, use the app.
              </h1>
              <p className="mt-5 text-lg text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl">
                Each bundle includes a Kindle and Apple Books compatible digital book, a printable PDF edition, and app access where included. Built for practical follow-through, not just reading.
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-3">
                {[
                  { icon: BookOpen, label: 'Kindle and Apple Books compatible' },
                  { icon: Download, label: 'Printable PDF included' },
                  { icon: Smartphone, label: 'App access with health bundles' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                      <Icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mb-2" />
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{item.label}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#book-bundles" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3 text-sm font-black text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100">
                  Shop the bundles
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a href="#compare-books" className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-black text-gray-800 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800">
                  Compare books
                </a>
              </div>
            </div>
            <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5 shadow-xl">
              <div className="grid grid-cols-3 gap-3">
                {featured.map((book) => (
                  <img
                    key={book.slug}
                    src={book.image}
                    alt=""
                    className="aspect-[2/3] w-full object-cover rounded-xl shadow-md"
                    loading="lazy"
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 sm:px-6 pb-10">
          <div className="max-w-6xl mx-auto rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-600 dark:text-indigo-400">Explore by goal</p>
                <h2 className="mt-1 text-2xl font-black text-gray-900 dark:text-white">Click a book to jump to the full breakdown.</h2>
              </div>
              <Link to="/login?tab=book" className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-black text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                I already have a book code
              </Link>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {allBooks.map((book) => (
                <a
                  key={book.slug}
                  href={book.slug === 'macros-made-easy-for-women' ? '/books/macros-made-easy-for-women' : `#details-${book.slug}`}
                  onClick={() => trackBookViewContent(book)}
                  className="group rounded-2xl border border-gray-200 p-3 transition hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-indigo-50 dark:border-gray-800 dark:hover:border-indigo-800 dark:hover:bg-indigo-950/30"
                >
                  <div className="flex gap-3">
                    <img src={book.image} alt="" className="h-16 w-11 rounded-md object-cover shadow-sm" loading="lazy" />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{book.category}</p>
                      <p className="mt-1 text-sm font-black leading-tight text-gray-900 group-hover:text-indigo-700 dark:text-white dark:group-hover:text-indigo-300">{book.title}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="book-bundles" className="px-4 sm:px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            {successSlug && (
              <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="font-black">Payment received.</p>
                    {deliveryLoading && <p className="mt-1 text-sm">Verifying your purchase and preparing downloads...</p>}
                    {deliveryError && (
                      <p className="mt-1 text-sm">
                        Your payment was received, but downloads could not be verified on this page. Check the email used at checkout or contact support with your receipt.
                      </p>
                    )}
                    {delivery && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold">{delivery.title} is ready.</p>
                        <div className="mt-3 flex flex-col sm:flex-row gap-2">
                          <a
                            href={delivery.files.pdf}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-gray-800"
                          >
                            <Download className="h-4 w-4" />
                            PDF
                          </a>
                          <a
                            href={delivery.files.epub}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700"
                          >
                            <BookOpen className="h-4 w-4" />
                            Kindle / Apple Books
                          </a>
                        </div>
                        {delivery.appAccess && (
                          <div className="mt-4 rounded-xl border border-emerald-200 bg-white/70 p-4 dark:border-emerald-800 dark:bg-gray-950/30">
                            <p className="text-sm font-bold">Included app access code</p>
                            <p className="mt-1 font-mono text-2xl font-black tracking-[0.2em]">{delivery.appAccess.code}</p>
                            <Link to="/login?tab=book" className="mt-2 inline-flex text-sm font-bold text-emerald-700 underline dark:text-emerald-200">
                              Open app access page
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="mt-3 text-xs opacity-80">A delivery email is also sent when email delivery is configured.</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}

            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black">Book bundles</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Secure checkout is handled by Stripe.</p>
              </div>
            </div>

            <div className="grid gap-5">
              {featured.map((book) => (
                <BookCard key={book.slug} book={book} />
              ))}
            </div>

            {otherBooks.length > 0 && (
              <>
                <h2 className="mt-12 mb-5 text-2xl font-black">Other guides</h2>
                <div className="grid gap-5">
                  {otherBooks.map((book) => (
                    <BookCard key={book.slug} book={book} />
                  ))}
                </div>
              </>
            )}

            <section className="mt-16">
              <div className="mb-6">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-600 dark:text-indigo-400">A closer look</p>
                <h2 className="mt-2 text-3xl font-black text-gray-900 dark:text-white">What each bundle gives you</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                  The goal is not just another PDF on your desktop. Each guide is built around a specific problem, with worksheets and next steps that help readers act on the material.
                </p>
              </div>
              <div className="space-y-8">
                {allBooks.map((book, index) => (
                  <BookDetailPanel key={book.slug} book={book} index={index} />
                ))}
              </div>
            </section>

            <section id="compare-books" className="mt-16 scroll-mt-24 rounded-3xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900 sm:p-7">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-600 dark:text-indigo-400">Compare</p>
                  <h2 className="mt-2 text-3xl font-black text-gray-900 dark:text-white">Choose by the result you want.</h2>
                </div>
                <p className="max-w-xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                  Health titles include app access where listed. Parenting and exam-prep titles focus on printable routines and study tools.
                </p>
              </div>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800">
                      <th className="py-3 pr-4">Book</th>
                      <th className="py-3 pr-4">Best for</th>
                      <th className="py-3 pr-4">Core tools</th>
                      <th className="py-3 pr-4">Price</th>
                      <th className="py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {allBooks.map((book) => (
                      <tr key={book.slug} className="align-top">
                        <td className="py-4 pr-4 font-black text-gray-900 dark:text-white">{book.title}</td>
                        <td className="py-4 pr-4 text-gray-600 dark:text-gray-300">{book.bestFor}</td>
                        <td className="py-4 pr-4 text-gray-600 dark:text-gray-300">{book.tools.slice(0, 2).join(' - ')}</td>
                        <td className="py-4 pr-4 font-black text-gray-900 dark:text-white">${book.price}</td>
                        <td className="py-4">
                          <a href={book.slug === 'macros-made-easy-for-women' ? '/books/macros-made-easy-for-women' : `#details-${book.slug}`} className="font-black text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">
                            Details
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-16 rounded-3xl bg-gray-900 p-6 text-white dark:bg-white dark:text-gray-900 sm:p-8">
              <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-300 dark:text-indigo-600">Book + action system</p>
                  <h2 className="mt-2 text-3xl font-black">Start with the guide that matches your season.</h2>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-300 dark:text-gray-600">
                    Buy once, download the digital files, print the pages you want, and use the app access code included with health bundles to keep the plan visible day to day.
                  </p>
                </div>
                <a href="#book-bundles" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-black text-gray-900 hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800">
                  Back to bundles
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </section>
          </div>
        </section>
      </main>

      {checkoutBook && (
        <div className="fixed inset-0 z-50 bg-gray-950/75 px-4 py-6 sm:py-10 overflow-y-auto">
          <div className="mx-auto max-w-3xl rounded-2xl bg-white dark:bg-gray-950 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-800 px-5 py-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">Stripe secure checkout</p>
                <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">{checkoutBook.title}</h2>
              </div>
              <button
                type="button"
                onClick={closeCheckout}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-200 dark:hover:bg-gray-900"
              >
                Close
              </button>
            </div>
            <div className="min-h-[620px] p-4 sm:p-6">
              {stripePromise ? (
                <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              ) : (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  Stripe checkout is missing the publishable key.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BooksPage;
